import { useState } from 'react';
import { ShieldCheck, ArrowLeft, Copy, Check, AlertTriangle } from 'lucide-react';
import { useSetup2FA, useVerifySetup2FA, useDisable2FA, useRegenerateBackupCodes } from '@/hooks/use-two-factor';
import { useAuthStore } from '@/stores/auth-store';

type Step = 'overview' | 'qr' | 'verify' | 'backup-codes' | 'disable' | 'regenerate';

export function TwoFactorSetupPage() {
  const { user, setAuth, tenant } = useAuthStore();
  const setup2FA = useSetup2FA();
  const verifySetup = useVerifySetup2FA();
  const disable2FA = useDisable2FA();
  const regenCodes = useRegenerateBackupCodes();

  const isEnabled = user?.twoFactorEnabled;
  const [step, setStep] = useState<Step>('overview');
  const [qrData, setQrData] = useState<{ qrCodeUrl: string; secret: string } | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const handleStartSetup = async () => {
    setError('');
    try {
      const res = await setup2FA.mutateAsync();
      setQrData({ qrCodeUrl: res.data.qrCodeUrl, secret: res.data.secret });
      setStep('qr');
    } catch (err: any) {
      setError(err?.error?.message || 'Failed to start 2FA setup');
    }
  };

  const handleVerifySetup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!code.trim()) {
      setError('Verification code is required');
      return;
    }
    try {
      const res = await verifySetup.mutateAsync({ code: code.trim() });
      setBackupCodes(res.data.backupCodes);
      if (user) setAuth({ ...user, twoFactorEnabled: true }, tenant);
      setStep('backup-codes');
      setCode('');
    } catch (err: any) {
      setError(err?.error?.message || 'Invalid verification code');
    }
  };

  const handleDisable = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!code.trim()) {
      setError('Verification code is required');
      return;
    }
    try {
      await disable2FA.mutateAsync({ code: code.trim() });
      if (user) setAuth({ ...user, twoFactorEnabled: false }, tenant);
      setCode('');
      window.location.hash = '#/settings';
    } catch (err: any) {
      setError(err?.error?.message || 'Failed to disable 2FA');
    }
  };

  const handleRegenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!code.trim()) {
      setError('Verification code is required');
      return;
    }
    try {
      const res = await regenCodes.mutateAsync({ code: code.trim() });
      setBackupCodes(res.data.backupCodes);
      setStep('backup-codes');
      setCode('');
    } catch (err: any) {
      setError(err?.error?.message || 'Failed to regenerate backup codes');
    }
  };

  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="mx-auto max-w-lg space-y-6">
      <div className="flex items-center gap-3">
        {isEnabled && (
          <a href="#/settings" className="rounded-lg p-1.5 hover:bg-muted">
            <ArrowLeft className="h-5 w-5" />
          </a>
        )}
        <div>
          <h1 className="text-xl font-semibold">Two-Factor Authentication</h1>
          <p className="text-sm text-muted-foreground">
            {isEnabled
              ? 'Manage your two-factor authentication settings'
              : 'Add an extra layer of security to your account'}
          </p>
        </div>
      </div>

      {/* Overview */}
      {step === 'overview' && (
        <div className="space-y-4 rounded-xl border border-border bg-white p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <ShieldCheck className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="font-medium">{isEnabled ? '2FA is enabled' : '2FA is not enabled'}</h3>
              <p className="text-sm text-muted-foreground">
                {isEnabled
                  ? 'Your account is protected with two-factor authentication.'
                  : 'Add an authenticator app for additional security.'}
              </p>
            </div>
          </div>

          {error && <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>}

          {isEnabled ? (
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setStep('regenerate');
                  setError('');
                  setCode('');
                }}
                className="flex-1 rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
              >
                Regenerate backup codes
              </button>
              <button
                onClick={() => {
                  setStep('disable');
                  setError('');
                  setCode('');
                }}
                className="flex-1 rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
              >
                Disable 2FA
              </button>
            </div>
          ) : (
            <button
              onClick={handleStartSetup}
              disabled={setup2FA.isPending}
              className="h-10 w-full rounded-lg bg-primary text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {setup2FA.isPending ? 'Setting up...' : 'Enable 2FA'}
            </button>
          )}
        </div>
      )}

      {/* QR Code Step */}
      {step === 'qr' && qrData && (
        <div className="space-y-4 rounded-xl border border-border bg-white p-6">
          <h3 className="font-medium">Scan QR code</h3>
          <p className="text-sm text-muted-foreground">
            Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
          </p>

          <div className="flex justify-center">
            <img src={qrData.qrCodeUrl} alt="2FA QR Code" className="h-48 w-48 rounded-lg border" />
          </div>

          <div>
            <p className="mb-1 text-xs text-muted-foreground">Or enter this key manually:</p>
            <div className="rounded-lg bg-muted/50 px-3 py-2 font-mono text-sm tracking-wider">{qrData.secret}</div>
          </div>

          <button
            onClick={() => setStep('verify')}
            className="h-10 w-full rounded-lg bg-primary text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Continue
          </button>
        </div>
      )}

      {/* Verify Step */}
      {step === 'verify' && (
        <div className="space-y-4 rounded-xl border border-border bg-white p-6">
          <h3 className="font-medium">Verify setup</h3>
          <p className="text-sm text-muted-foreground">
            Enter the 6-digit code from your authenticator app to confirm the setup.
          </p>

          <form onSubmit={handleVerifySetup} className="space-y-4">
            {error && <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>}

            <input
              type="text"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                setError('');
              }}
              autoFocus
              autoComplete="one-time-code"
              inputMode="numeric"
              maxLength={6}
              className="h-10 w-full rounded-lg border border-input px-3 text-center font-mono text-lg tracking-widest outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              placeholder="000000"
            />

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setStep('qr')}
                className="flex-1 rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={verifySetup.isPending}
                className="flex-1 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {verifySetup.isPending ? 'Verifying...' : 'Verify & Enable'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Backup Codes */}
      {step === 'backup-codes' && (
        <div className="space-y-4 rounded-xl border border-border bg-white p-6">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            <h3 className="font-medium">Save your backup codes</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            These codes can be used to access your account if you lose your authenticator device. Each code can only be
            used once. Store them somewhere safe.
          </p>

          <div className="grid grid-cols-2 gap-2 rounded-lg bg-muted/50 p-4">
            {backupCodes.map((bc, i) => (
              <div key={i} className="font-mono text-sm">
                {bc}
              </div>
            ))}
          </div>

          <button
            onClick={copyBackupCodes}
            className="flex h-10 w-full items-center justify-center gap-2 rounded-lg border border-border text-sm font-medium hover:bg-muted"
          >
            {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
            {copied ? 'Copied!' : 'Copy backup codes'}
          </button>

          <button
            onClick={() => {
              setBackupCodes([]);
              // If 2FA was just enabled, go to dashboard; otherwise back to overview
              if (isEnabled) {
                window.location.hash = '#/';
              }
              setStep('overview');
            }}
            className="h-10 w-full rounded-lg bg-primary text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Done
          </button>
        </div>
      )}

      {/* Disable 2FA */}
      {step === 'disable' && (
        <div className="space-y-4 rounded-xl border border-border bg-white p-6">
          <h3 className="font-medium">Disable two-factor authentication</h3>
          <p className="text-sm text-muted-foreground">
            Enter your authenticator code or a backup code to disable 2FA.
          </p>

          <form onSubmit={handleDisable} className="space-y-4">
            {error && <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>}

            <input
              type="text"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                setError('');
              }}
              autoFocus
              autoComplete="one-time-code"
              className="h-10 w-full rounded-lg border border-input px-3 text-center font-mono text-lg tracking-widest outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              placeholder="000000"
            />

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setStep('overview');
                  setCode('');
                  setError('');
                }}
                className="flex-1 rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={disable2FA.isPending}
                className="flex-1 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {disable2FA.isPending ? 'Disabling...' : 'Disable 2FA'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Regenerate Backup Codes */}
      {step === 'regenerate' && (
        <div className="space-y-4 rounded-xl border border-border bg-white p-6">
          <h3 className="font-medium">Regenerate backup codes</h3>
          <p className="text-sm text-muted-foreground">
            Enter your authenticator code to generate new backup codes. This will invalidate all existing backup codes.
          </p>

          <form onSubmit={handleRegenerate} className="space-y-4">
            {error && <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>}

            <input
              type="text"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                setError('');
              }}
              autoFocus
              autoComplete="one-time-code"
              inputMode="numeric"
              maxLength={6}
              className="h-10 w-full rounded-lg border border-input px-3 text-center font-mono text-lg tracking-widest outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              placeholder="000000"
            />

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setStep('overview');
                  setCode('');
                  setError('');
                }}
                className="flex-1 rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={regenCodes.isPending}
                className="flex-1 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {regenCodes.isPending ? 'Generating...' : 'Generate new codes'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
